import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { duckify, filterDuck } from 'utils/duckify';
import { BillingCategoryType } from 'propTypes';
import { billingCategoryGroups, modals } from 'ducks';
import ModalComponent from 'components/ModalComponent';
import FALabel from 'components/shared/FALabel';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import FontAwesome from '../../utils/FA';

const ReorganizeCategoryModal = ({ category, availableCategories, moveCategory }) => {
  const [targetGroupId, internalSetTargetId] = useState(category.parent_id);
  const [categoryType, setCategoryType] = useState(category.category_type);

  const setTargetGroupId = (targetGroupId) => {
    internalSetTargetId(targetGroupId);
    // Top level categories must be charge categories
    if (targetGroupId === category.id) {
      setCategoryType('charge');
    }
  };

  const dirty = targetGroupId !== category.parent_id || categoryType !== category.category_type;

  // exclude group
  const targetGroups = (
    availableCategories.ready
      ? availableCategories.filter(c => c.id !== category.id)
      : []
  );
  const newGroupOption = !!category.parent_id;
  const buttons = [
    <button
      type='button'
      className='btn btn-danger'
      disabled={!dirty}
      onClick={() => moveCategory(category.id, targetGroupId, categoryType)}
    >
      Move Category
    </button>
  ];

  return (
    <ModalComponent title='Move Category' buttons={buttons}>
      <p>
        You can move this category&nbsp;
        <strong>{category.name}</strong>
        &nbsp;to one of the options below:
      </p>

      { availableCategories.ready || (
        <p className='text-center'>
          <FALabel label='Loading...' icon='spinner fa-spin' />
        </p>
      )}

      { availableCategories.ready && (
        <div className='mb-3'>
          <select name='targetGroupId' value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)} style={{ width: 'auto' }}>
            <option value=''>
              Choose one
            </option>
            {
              targetGroups.map(c => (
                <option key={c.id} value={c.id}>
                  {`Subcategory of: ${c.name}${c.active ? '' : ' (inactive)'}`}
                </option>
              ))
            }
            {newGroupOption && <option disabled>--- or ---</option>}
            {newGroupOption && (
              <option value={category.id}>
                {`Make ${category.name} a new Category Group`}
              </option>
            )}
          </select>

          <p>
            You can also choose the type of category below:
          </p>

          <div className='mb-3'>
            <select
              name='categoryType'
              value={categoryType}
              onChange={e => setCategoryType(e.target.value)}
              disabled={targetGroupId === category.id}
            >
              <option value='charge'>Charge</option>
              <option value='credit'>Credit</option>
            </select>
          </div>

          {
            targetGroupId === category.id && category.category_type === 'credit' && (
              <div className='alert alert-warning'>
                <FontAwesome icon={faExclamationTriangle} postNbsp />
                By making this category a new group the category type will change
                from <em>Credit</em> to <em>Charge</em>.
              </div>
            )
          }

        </div>
      )}

      { availableCategories.ready && targetGroups.length === 0 && !category.parent_id && (
        <div className='alert alert-warning'>
        There are no valid categories for moving this category.
        </div>
      )}
    </ModalComponent>
  );
};

ReorganizeCategoryModal.propTypes = {
  category: BillingCategoryType.isRequired,
  availableCategories: PropTypes.arrayOf(BillingCategoryType).isRequired,
  moveCategory: PropTypes.func.isRequired
};

const mapDucksToProps = (props) => {
  const categoryQuery = {};
  if (props.category.contact) {
    categoryQuery.contact_only = true;
  } else {
    categoryQuery.non_contact_only = true;
  }

  return {
    availableCategories: filterDuck(billingCategoryGroups, categoryQuery)
  };
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  moveCategory: (id, targetGroupId, categoryType) => {
    dispatch(billingCategoryGroups.actions.moveCategory(id, targetGroupId, categoryType))
        .then(() => {
          dispatch(billingCategoryGroups.actions.markDirty());
          dispatch(modals.actions.hide('ReorganizeCategoryModal'));
        })
        .catch((response) => {
          dispatch(billingCategoryGroups.actions.markDirty());
          return dispatch(modals.actions.show('UnexpectedError', {message: response.message}));
        });
  }
});

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(ReorganizeCategoryModal);
